import {
  CheckMetadata,
  CheckMetadataItem,
  CheckMetadataItemType,
  renderMatchResultCell,
  renderStatusCell,
} from 'dashboard/components/ChecksTable/computeCheckMetadata';
import Pii from 'dashboard/components/Pii';
import {
  BusinessWebsiteIdentityComparison,
  BusinessWebsiteWebsiteComparison,
  CheckAttributeMatch,
  CheckName,
  CheckStatus,
  Match,
} from 'dashboard/models/check';
import { VerificationBusinessWebsite } from 'dashboard/models/verification';
import { sortChecks } from 'lib/utils/checks';

const WEBSITE_COMPARISON_CHECK_NAME = CheckName.BusinessWebsiteWebsiteComparison;
const IDENTITY_COMPARISON_CHECK_NAME = CheckName.BusinessWebsiteIdentityComparison;

const matchLevelRank: Record<Match, number> = {
  missing: -1,
  none: 0,
  partial: 1,
  full: 2,
};

export const computeBusinessWebsiteAttributeChecksMetadata = (
  verification: VerificationBusinessWebsite,
): CheckMetadata => {
  const isRedacted = verification.attributes?.['redacted-at'] != null;

  const sortedChecks = sortChecks(verification.attributes?.checks || []);
  const websiteComparisonCheck = sortedChecks.find(
    (check) => check.name === WEBSITE_COMPARISON_CHECK_NAME,
  ) as BusinessWebsiteWebsiteComparison;

  let extractedName, extractedAnalysis, matchLevel, matchAnalysis;

  const identityRecordBusinessNameResults =
    websiteComparisonCheck.metadata?.['identity-record-results']?.['business-names']?.[0];
  if (identityRecordBusinessNameResults != null) {
    extractedName = identityRecordBusinessNameResults['extracted-value'];
    extractedAnalysis = identityRecordBusinessNameResults['extracted-analysis'];
    matchLevel = identityRecordBusinessNameResults['match-level'];
    matchAnalysis = identityRecordBusinessNameResults['match-analysis'];
  } else {
    const extractedBusinessNameResults =
      websiteComparisonCheck.metadata?.['extracted-business-name-results']?.[0];

    extractedName = extractedBusinessNameResults?.['extracted-name'];
    extractedAnalysis = extractedBusinessNameResults?.['extracted-analysis'];
    matchLevel = extractedBusinessNameResults?.['match-level'];
    matchAnalysis = extractedBusinessNameResults?.['match-analysis'];
  }

  const identityComparisonCheck = sortedChecks.find(
    (check) => check.name === IDENTITY_COMPARISON_CHECK_NAME,
  ) as BusinessWebsiteIdentityComparison;

  if (identityComparisonCheck && identityComparisonCheck.status !== CheckStatus.NotApplicable) {
    const identityRecordResults = identityComparisonCheck?.metadata?.['identity-record-results'];
    const header = ['Property', 'Extracted Value', 'Match Result', 'Status'];
    const rows = [];

    if (identityRecordResults) {
      const matchLevelMinimums = verification.attributes['identity-check-match-level-minimums'];
      const businessNameMatchLevel = (matchLevelMinimums?.['business-name'] as Match) || 'none';
      if (matchLevelRank[businessNameMatchLevel] > matchLevelRank['none']) {
        const business = identityRecordResults['business-names'][0];
        const row = [
          renderPropertyCell('Business Name', isRedacted),
          renderPropertyCell(business['extracted-value'], isRedacted),
          renderMatchResultCell(
            business['match-level'] as Match,
            undefined,
            businessNameMatchLevel,
          ),
          renderStatusCell(
            matchLevelRank[business['match-level']] >= matchLevelRank[businessNameMatchLevel]
              ? CheckStatus.Passed
              : CheckStatus.Failed,
          ),
        ];
        rows.push(row);
      }
      const urlBusinessNameMatchLevel =
        (matchLevelMinimums?.['url-business-name'] as Match) || 'none';
      if (matchLevelRank[urlBusinessNameMatchLevel] > matchLevelRank['none']) {
        const urlBusiness = identityRecordResults['business-names'][1];
        const row = [
          renderPropertyCell('URL Business Name', isRedacted),
          renderPropertyCell(urlBusiness['extracted-value'], isRedacted),
          renderMatchResultCell(
            urlBusiness['match-level'] as Match,
            undefined,
            urlBusinessNameMatchLevel,
          ),
          renderStatusCell(
            matchLevelRank[urlBusiness['match-level']] >= matchLevelRank[urlBusinessNameMatchLevel]
              ? CheckStatus.Passed
              : CheckStatus.Failed,
          ),
        ];
        rows.push(row);
      }
      const addressMatchLevel = (matchLevelMinimums?.['address'] as Match) || 'none';
      if (matchLevelRank[addressMatchLevel] > matchLevelRank['none']) {
        const row = [];
        row.push(renderPropertyCell('Address', isRedacted));
        const addresses = identityRecordResults['addresses'];
        if (addresses.length > 0) {
          const [topAddress] = addresses.sort(
            (a, b) => matchLevelRank[b['match-level']] - matchLevelRank[a['match-level']],
          );
          row.push(
            renderPropertyCell(topAddress['extracted-value'], isRedacted),
            renderMatchResultCell(topAddress['match-level'] as Match, undefined, addressMatchLevel),
            renderStatusCell(
              matchLevelRank[topAddress['match-level']] >= matchLevelRank[addressMatchLevel]
                ? CheckStatus.Passed
                : CheckStatus.Failed,
            ),
          );
        } else {
          row.push(
            renderPropertyCell('No Address Found', isRedacted),
            renderMatchResultCell(CheckAttributeMatch.None, undefined, addressMatchLevel),
            renderStatusCell(CheckStatus.Failed),
          );
        }
        rows.push(row);
      }
    }
    return {
      metadata: [
        {
          type: CheckMetadataItemType.Table,
          heading: {
            title: '',
          },
          subheading: '',
          rowHeaders: header,
          rows: rows,
          tableEmptyState: 'N/A',
        },
      ] as CheckMetadataItem[],
      hideIfVerificationPassed: false,
    };
  }

  return {
    metadata: [
      {
        type: CheckMetadataItemType.Table,
        heading: {
          title: 'Website Comparison Check',
        },
        subheading: '',
        rowHeaders: [
          'Property',
          'Extracted Name',
          'Extraction Analysis',
          'Match Result',
          'Match Analysis',
        ],
        rows: [
          [
            'Business Name',
            renderPropertyCell(extractedName || '', isRedacted),
            renderPropertyCell(extractedAnalysis || '', isRedacted),
            renderMatchResultCell((matchLevel as Match) || ''),
            renderPropertyCell(matchAnalysis || '', isRedacted),
          ],
        ],
        tableEmptyState: 'No website comparison check run',
      },
    ] as CheckMetadataItem[],
    hideIfVerificationPassed: false,
  };
};

function renderPropertyCell(value: string, isRedacted: boolean) {
  return <Pii isRedacted={isRedacted}>{value}</Pii>;
}

export function getStatusForCheckRow(verification: VerificationBusinessWebsite) {
  const sortedChecks = sortChecks(verification.attributes?.checks || []);

  const websiteComparisonCheck = sortedChecks.find(
    (check) => check.name === CheckName.BusinessWebsiteWebsiteComparison,
  );

  const identityComparisonCheck = sortedChecks.find(
    (check) => check.name === CheckName.BusinessWebsiteIdentityComparison,
  );

  if (identityComparisonCheck && identityComparisonCheck.status !== CheckStatus.NotApplicable) {
    return identityComparisonCheck.status;
  }

  return websiteComparisonCheck?.status;
}
